import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AudioTourConfig, TourStop, audioTours } from '../data/audioTours';

interface AudioTourState {
  // Tour config
  tour: AudioTourConfig | null;
  citySlug: string | null;

  // Playback
  currentStop: number; // -1 = intro
  isPlaying: boolean;
  progress: number;
  duration: number;
  currentTime: number;
  audioLoading: boolean;
  audioError: boolean;

  // UI state
  isMinimized: boolean;
  isActive: boolean; // true if a tour is running (minimized or full)
  walkingMode: boolean;
  showWalkingNav: boolean;
  visitedStops: Set<number>;

  // Current data helpers
  currentStopData: TourStop | null;
  currentName: string;
  currentImage: string;
  stopLabel: string;
}

interface AudioTourActions {
  startTour: (citySlug: string, startStop?: number, autoPlay?: boolean) => void;
  unlockAudio: (src?: string) => void;
  stopTour: () => void;
  minimize: () => void;
  expand: () => void;
  togglePlay: () => void;
  goToNext: () => void;
  goToPrev: () => void;
  goToStop: (index: number) => void;
  seekTo: (pct: number) => void;
  setWalkingMode: (on: boolean) => void;
  setShowWalkingNav: (show: boolean) => void;
  markVisited: (index: number) => void;
  audioRef: React.RefObject<HTMLAudioElement | null>;
}

const AudioTourContext = createContext<(AudioTourState & AudioTourActions) | null>(null);

export const useAudioTour = () => {
  const ctx = useContext(AudioTourContext);
  if (!ctx) throw new Error('useAudioTour must be used within AudioTourProvider');
  return ctx;
};

export const AudioTourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === 'it' ? 'it' : 'en';

  // Core state
  const [citySlug, setCitySlug] = useState<string | null>(null);
  const [currentStop, setCurrentStop] = useState(-1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [walkingMode, setWalkingMode] = useState(false);
  const [showWalkingNav, setShowWalkingNav] = useState(false);
  const [visitedStops, setVisitedStops] = useState<Set<number>>(new Set());

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const currentSrcRef = useRef<string>('');
  // Diventa true al primo play() partito da un tocco: prima di allora iOS blocca l'audio.
  const audioUnlockedRef = useRef(false);
  // Copie sempre aggiornate per i listener dell'elemento audio, registrati una volta sola.
  const isPlayingRef = useRef(false);
  const currentStopRef = useRef(-1);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  useEffect(() => { currentStopRef.current = currentStop; }, [currentStop]);

  const handlePlayError = useCallback((err: any) => {
    // play() interrotto dal caricamento della tappa successiva: nulla da segnalare.
    if (err && err.name === 'AbortError') return;
    // Manca il gesto dell'utente: non e' un audio rotto, basta rimettere il tasto Play.
    if (err && err.name === 'NotAllowedError') {
      setIsPlaying(false);
      return;
    }
    setAudioError(true);
  }, []);

  const tour = citySlug ? audioTours[citySlug] || null : null;

  // Get audio source for current stop
  const getAudioSrc = useCallback((stop: number) => {
    if (!tour) return '';
    if (stop === -1) return tour.introAudioFile[lang];
    return tour.stops[stop]?.audioFile[lang] || '';
  }, [tour, lang]);

  // Load audio when stop changes (NOT when isPlaying changes)
  useEffect(() => {
    if (!isActive || !tour) return;
    const audio = audioRef.current;
    if (!audio) return;

    const src = getAudioSrc(currentStop);
    // Se il gesto dell'utente ha gia' caricato questa sorgente, non ricaricarla:
    // load() interromperebbe il play() appena avviato dentro il tocco.
    if (!src || src === currentSrcRef.current) return;

    currentSrcRef.current = src;
    setAudioLoading(true);
    setAudioError(false);
    setProgress(0);
    setCurrentTime(0);
    audio.src = src;
    audio.load();
  }, [isActive, currentStop, tour, getAudioSrc]);

  // Audio event listeners (set up once)
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onCanPlay = () => {
      setAudioLoading(false);
      setDuration(audio.duration);
      // Riparte da sola solo se l'utente ha gia' sbloccato l'audio con un tocco:
      // senza gesto Safari rifiuta il play() e l'audioguida resterebbe muta.
      if (isPlayingRef.current) {
        if (audioUnlockedRef.current) {
          audio.play().catch(handlePlayError);
        } else {
          setIsPlaying(false);
        }
      }
    };

    const onTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
      setProgress(audio.duration ? (audio.currentTime / audio.duration) * 100 : 0);
    };

    const onEnded = () => {
      setIsPlaying(false);
      const stop = currentStopRef.current;
      if (stop >= 0) {
        setVisitedStops(prev => new Set(prev).add(stop));
      }
    };

    // Allinea lo stato ai comandi esterni (cuffie, schermata di blocco, centro di controllo).
    const onPlay = () => {
      audioUnlockedRef.current = true;
      setAudioError(false);
      setIsPlaying(true);
    };

    const onPause = () => {
      // Le pause dovute al cambio di sorgente non sono scelte dell'utente.
      if (audio.readyState < 2) return;
      setIsPlaying(false);
    };

    const onError = () => {
      setAudioLoading(false);
      setAudioError(true);
    };

    audio.addEventListener('canplay', onCanPlay);
    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.addEventListener('play', onPlay);
    audio.addEventListener('pause', onPause);
    audio.addEventListener('error', onError);

    return () => {
      audio.removeEventListener('canplay', onCanPlay);
      audio.removeEventListener('timeupdate', onTimeUpdate);
      audio.removeEventListener('ended', onEnded);
      audio.removeEventListener('play', onPlay);
      audio.removeEventListener('pause', onPause);
      audio.removeEventListener('error', onError);
    };
  }, [handlePlayError]);

  // Derived data
  const currentStopData = tour && currentStop >= 0 ? tour.stops[currentStop] : null;
  const currentName = currentStop === -1
    ? t('audioTour.introduction')
    : currentStopData ? t(currentStopData.nameKey) : '';
  const currentImage = currentStopData?.imageUrl || (tour?.stops[0]?.imageUrl || '');
  const stopLabel = currentStop === -1
    ? t('audioTour.intro')
    : tour ? `${currentStop + 1}/${tour.stops.length}` : '';

  // Actions
  // Sblocca l'elemento audio dentro il gesto dell'utente: su iOS un play() fuori dal
  // tocco viene rifiutato (NotAllowedError) e la guida non parte piu'.
  const unlockAudio = useCallback((src?: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    const wasUnlocked = audioUnlockedRef.current;
    audioUnlockedRef.current = true;

    if (src) {
      // Sorgente caricata e avviata dentro il tocco: e' lo sblocco piu' affidabile.
      if (currentSrcRef.current !== src) {
        currentSrcRef.current = src;
        audio.src = src;
        audio.load();
        setAudioLoading(true);
        setAudioError(false);
        setProgress(0);
        setCurrentTime(0);
      }
      audio.play().catch(handlePlayError);
      return;
    }

    if (wasUnlocked || !audio.paused) return;
    // Primo tocco su un comando che non avvia subito l'audio: play()+pause() muto,
    // serve solo a marcare l'elemento come autorizzato.
    const wasMuted = audio.muted;
    audio.muted = true;
    const started = audio.play();
    const restore = () => { audio.pause(); audio.muted = wasMuted; };
    if (started && typeof started.then === 'function') {
      started.then(restore).catch(() => { audio.muted = wasMuted; });
    } else {
      restore();
    }
  }, [handlePlayError]);

  const startTour = useCallback((slug: string, startStop?: number, autoPlay?: boolean) => {
    setCitySlug(slug);
    setCurrentStop(startStop !== undefined ? startStop : -1);
    // Niente partenza automatica se l'audio non e' ancora stato sbloccato da un tocco
    // (tipico dell'ingresso da una scheda con ?start=N): mostra il tasto Play pronto.
    setIsPlaying(autoPlay !== undefined ? autoPlay : audioUnlockedRef.current);
    setIsMinimized(false);
    setIsActive(true);
    setVisitedStops(new Set());
    setShowWalkingNav(false);
    // currentSrcRef non si azzera qui: stopTour lo ha gia' pulito e cosi' sopravvive
    // la sorgente appena caricata da unlockAudio dentro il gesto.
  }, []);

  const stopTour = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.src = ''; }
    currentSrcRef.current = '';
    // Togli anche la scheda dalla schermata di blocco: il tour e' chiuso.
    if ('mediaSession' in navigator) {
      try { navigator.mediaSession.metadata = null; } catch { /* non supportato */ }
    }
    setIsActive(false);
    setIsMinimized(false);
    setIsPlaying(false);
    setCitySlug(null);
    setCurrentStop(-1);
    setVisitedStops(new Set());
  }, []);

  const minimize = useCallback(() => {
    setIsMinimized(true);
  }, []);

  const expand = useCallback(() => {
    setIsMinimized(false);
  }, []);

  const togglePlay = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;
    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      // Siamo dentro il tocco dell'utente: qui il play() e' sempre concesso.
      audioUnlockedRef.current = true;
      setAudioError(false);
      audio.play().then(() => setIsPlaying(true)).catch(handlePlayError);
    }
  }, [isPlaying, handlePlayError]);

  const goToNext = useCallback(() => {
    if (!tour) return;
    setShowWalkingNav(false);
    if (currentStop < tour.stops.length - 1) {
      if (currentStop >= 0) {
        setVisitedStops(prev => new Set(prev).add(currentStop));
      }
      currentSrcRef.current = ''; // Force reload for new stop
      setCurrentStop(prev => prev + 1);
      setIsPlaying(true);
    }
  }, [tour, currentStop]);

  const goToPrev = useCallback(() => {
    if (currentStop > -1) {
      currentSrcRef.current = '';
      setCurrentStop(prev => prev - 1);
      setIsPlaying(true);
    }
  }, [currentStop]);

  const goToStop = useCallback((index: number) => {
    currentSrcRef.current = '';
    setCurrentStop(index);
    setIsPlaying(true);
    setShowWalkingNav(false);
  }, []);

  const seekTo = useCallback((pct: number) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;
    audio.currentTime = pct * duration;
  }, [duration]);

  const markVisited = useCallback((index: number) => {
    setVisitedStops(prev => new Set(prev).add(index));
  }, []);

  // ─── Comandi hardware / schermata di blocco (Media Session) ───
  // Su iOS l'API c'e' dal 15, quindi tutto sotto guardia.
  useEffect(() => {
    if (!('mediaSession' in navigator) || typeof window.MediaMetadata !== 'function') return;
    if (!isActive || !tour || !currentName) return;
    try {
      navigator.mediaSession.metadata = new window.MediaMetadata({
        title: currentName,
        artist: tour.cityName,
        album: t('audioTour.audioGuide'),
        artwork: currentImage ? [{ src: currentImage, sizes: '512x512', type: 'image/jpeg' }] : [],
      });
    } catch {
      // metadati non disponibili: la riproduzione continua comunque
    }
  }, [isActive, tour, currentName, currentImage, t]);

  useEffect(() => {
    if (!('mediaSession' in navigator) || !isActive) return;
    const setHandler = (action: MediaSessionAction, handler: (() => void) | null) => {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch {
        // azione non supportata dal browser
      }
    };
    setHandler('play', () => {
      audioUnlockedRef.current = true;
      audioRef.current?.play().catch(handlePlayError);
    });
    setHandler('pause', () => audioRef.current?.pause());
    setHandler('previoustrack', goToPrev);
    setHandler('nexttrack', goToNext);
    return () => {
      setHandler('play', null);
      setHandler('pause', null);
      setHandler('previoustrack', null);
      setHandler('nexttrack', null);
    };
  }, [isActive, goToPrev, goToNext, handlePlayError]);

  const value: AudioTourState & AudioTourActions = {
    tour, citySlug, currentStop, isPlaying, progress, duration, currentTime,
    audioLoading, audioError, isMinimized, isActive, walkingMode, showWalkingNav,
    visitedStops, currentStopData, currentName, currentImage, stopLabel,
    startTour, unlockAudio, stopTour, minimize, expand, togglePlay, goToNext, goToPrev,
    goToStop, seekTo, setWalkingMode, setShowWalkingNav, markVisited, audioRef,
  };

  return (
    <AudioTourContext.Provider value={value}>
      {/* Global audio element - persists across all pages */}
      <audio ref={audioRef} preload="auto" />
      {children}
    </AudioTourContext.Provider>
  );
};
